import { StatusCodes } from 'http-status-codes';
import { createContext, MiddlewareFunction } from 'react-router';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { Settings, settingsContext } from '~/app/settingsMiddleware';
import { AppState } from '~/stores/app';
import api from '~/utils/api';
import { identity } from '~/utils/compute';
import { ResponseError } from '~/utils/errors';
import { forceReplay, identifyUser } from '~/utils/observability';
import { appStoreContext, redirect } from '~/utils/routing';
import {
  crewOrderSchema,
  storeParamSchema,
  typeGuardFromSchema,
} from '~/utils/schemas';
import { AsyncReturnType } from '~/utils/types';

const paramsSchema = z.object({
  idFromPlatform: z.string(),
  store: storeParamSchema.shape.store.optional(),
});

const searchSchema = z.object({
  isGiftReturn: z
    .string()
    .optional()
    .transform((val) => val?.toLowerCase() === 'true'),
});

const getOrder = (
  { idFromPlatform, store }: z.infer<typeof paramsSchema>,
  { isGiftReturn }: z.infer<typeof searchSchema>,
  settings: Settings | null,
) =>
  store && settings
    ? api.fetchCrewOrder({
        params: { storefrontId: settings.storefrontId },
        query: { idFromPlatform, isGiftReturn },
      })
    : api.fetchProtectionOrder({
        query: { idFromPlatform },
      });

const isCrewOrder = typeGuardFromSchema(crewOrderSchema);

const isValidOrder = (
  params: z.infer<typeof paramsSchema>,
  order: AppState['order'],
  isCrewOrderExpected: boolean,
) =>
  !!order &&
  order.idFromPlatform === params.idFromPlatform &&
  (params.store === undefined || params.store === order.storeSlug) &&
  // reusing a cached protection order here leaves crewOrderContext unset for every crew loader
  (!isCrewOrderExpected || isCrewOrder(order));

type ProtectionOrder = AsyncReturnType<typeof api.fetchProtectionOrder>;
export type CrewOrder = AsyncReturnType<typeof api.fetchCrewOrder>;
export type Order = ProtectionOrder | CrewOrder;
export const orderContext = createContext<Order>();
export const crewOrderContext = createContext<CrewOrder>();

// enable the order to be available to the app shell via the app context
export const middleware: MiddlewareFunction = async ({
  params,
  request,
  context,
}) => {
  try {
    const appStore = context.get(appStoreContext);
    const validParams = paramsSchema.parse(params);
    // if there is a store param, we should have settings in context from the app shell middleware
    const settings = validParams.store ? context.get(settingsContext) : null;

    const validSearch = zfd
      .formData(searchSchema)
      .parse(new URL(request.url).searchParams);
    const state = appStore.getState();
    const isCrewOrderExpected = !!validParams.store && !!settings;

    if (!isValidOrder(validParams, state.order, isCrewOrderExpected)) {
      // clear the current invalid order
      appStore.dispatch(appStore.actions.invalidateOrder());

      await getOrder(validParams, validSearch, settings).then((order) =>
        appStore.dispatch(appStore.actions.setOrder(order)),
      );
    }

    const { order } = appStore.getState();

    // order should be set at this point, checking for TS sake
    if (!order) {
      throw new Error('Missing order on state');
    }

    identifyUser({
      email: order.email,
      storeName: order.storeName,
      orderNo: order.orderNo,
      orderIdFromPlatform: order.idFromPlatform,
      storeId: order.storeId,
    });

    forceReplay(order.storeId);

    if (isCrewOrder(order)) {
      context.set(crewOrderContext, order);
    }

    context.set(orderContext, order);
  } catch (cause) {
    const error = match(cause)
      .with(
        P.instanceOf(ResponseError).and({
          response: { status: StatusCodes.NOT_FOUND },
        }),
        () => redirect(`/${params.store ?? ''}`),
      )
      .with(P.instanceOf(Error), identity)
      .otherwise(() => new Error('Order Middleware error', { cause }));

    throw error;
  }
};
